import { find } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikSelectOption } from 'components';
import { AdditionalOrderColumn, FilterDefinitionType, OrdersTab } from 'enums';
import { Orders } from 'features/orders';
import { useCurrencies, useOrdersContext, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { filtersUtils, formUtils, orderUtils, requisitesUtils } from 'utils';

type Props = {
  tab: OrdersTab;
  additionalFilters?: FilterDefinition<OrderFilters>[];
  additionalColumns?: AdditionalOrderColumn[];
  additionalSortingFields?: FormikSelectOption[];
};

export const ManageOrdersList: React.FC<Props> = ({
  tab,
  additionalFilters = [],
  additionalSortingFields = [],
  additionalColumns = [],
}) => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { paymentTypes, banks, p2pProviders } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();
  const { shops, shopGroups, merchants, traders } = useOrdersContext();

  const filters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      {
        label: tFilters('customer'),
        name: 'customerId',
        type: FilterDefinitionType.Text,
      },
      {
        label: tFilters('trader'),
        name: 'traderId',
        type: FilterDefinitionType.User,
        users: traders,
        hidden: additionalColumns.includes(AdditionalOrderColumn.P2PProvider),
      },
      {
        label: tFilters('merchant'),
        name: 'merchantId',
        type: FilterDefinitionType.User,
        users: merchants,
      },
      {
        label: tFilters('shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        shops,
      },
      {
        label: tFilters('shop_group'),
        name: 'shopGroupId',
        type: FilterDefinitionType.ShopGroup,
        shopGroups,
        hidden: !shopGroups,
      },
      {
        label: tFilters('external_order_id'),
        name: 'externalOrderId',
        type: FilterDefinitionType.Text,
      },
      {
        label: tFilters('external_order_ids'),
        name: 'externalOrderIds',
        type: FilterDefinitionType.TextArea,
      },
      {
        label: tFilters('payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tFilters('bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
      {
        label: tFilters('requisites_id'),
        name: 'requisitesId',
        type: FilterDefinitionType.Text,
        format: 'uuid',
        hidden: tab === OrdersTab.ProviderDispute,
      },
      {
        label: tFilters('requisites_verification_status'),
        name: 'requisitesVerificationStatus',
        type: FilterDefinitionType.Select,
        options: orderUtils.getOrderRequisitesVerificationStatusOptions(),
        getDisplayName: orderUtils.getOrderRequisitesVerificationStatusLabel,
      },
      filtersUtils.getBooleanStatusFilter<OrderFilters>(
        tFilters('is_counterparty_flow'),
        'isCounterpartyFlow',
      ),
      {
        label: tFilters('p2p_provider'),
        name: 'p2pProviderId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(p2pProviders),
        getDisplayName: (value: string) =>
          find(p2pProviders, { id: value })?.name,
        hidden: additionalColumns.includes(AdditionalOrderColumn.Trader),
      },
      ...requisitesUtils.getRequisitesFieldsFilters<OrderFilters>(),
      ...requisitesUtils.getP2PProviderRequisitesFieldsFilters<OrderFilters>(),
    ],
    [
      tFilters,
      traders,
      additionalColumns,
      merchants,
      shops,
      shopGroups,
      paymentTypes,
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      tab,
      p2pProviders,
    ],
  );

  return (
    <Orders
      tab={tab}
      additionalFilters={[...filters, ...additionalFilters]}
      additionalSortingFields={additionalSortingFields}
      additionalColumns={[
        ...(tab === OrdersTab.RequisitesVerification
          ? [AdditionalOrderColumn.RequisitesVerificationResult]
          : [
              AdditionalOrderColumn.CustomerContacts,
              AdditionalOrderColumn.Merchant,
              AdditionalOrderColumn.CustomerPayment,
            ]),
        ...additionalColumns,
      ]}
    />
  );
};
